import React, { useMemo } from 'react'
import {
  LucideIcon, CircleCheckBig } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tab, type TabIcon } from '../../../hooks/multiWindow'
import { DormantIcon, DormantOpenIcon } from '../../../components/icons/DormantIcon'
import { UpcomingIcon, UpcomingOpenIcon } from '../../../components/icons/UpcomingIcon'
import { GoalsIcon } from '../../../components/icons/GoalsIcon'
import { QuickNotesFilledIcon, QuickNotesEmptyIcon, QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../components/icons'
import { useSidebarData } from './SidebarDataContext'
// Gewohnheiten/Habits vorübergehend in der Navigation ausgeblendet — Import bleibt für späteres Reaktivieren erhalten
// import { RocketIcon } from '../../../components/icons/RocketIcon'
import { RecurringIcon } from '../../../components/icons/RecurringIcon'
import { SidebarSection } from './SidebarSection'
import { SidebarItem } from './SidebarItem'
import { ProjectsSection } from './ProjectsSection'
import { FavoritesSection } from './FavoritesSection'
import { dispatchAppEvent } from '@/src/renderer/lib/events'

interface WorkSidebarContentProps {
  activeTab?: Tab
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: TabIcon }) => void
  tabs?: Tab[]
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

export const WorkSidebarContent: React.FC<WorkSidebarContentProps> = ({ activeTab, openTab, navigateActiveTab, tabs = [], onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace }) => {
  const { t } = useTranslation()
  // Aus dem Halter oberhalb des Wechsels (`SidebarDataProvider`): sonst starteten Zahl und Symbol bei jedem
  // Neu-Einhängen bei `0`/`false` (#285).
  const { quickNotesHasEntries, upcomingCount } = useSidebarData('work')
  const quickNotesTabOpen = tabs.some(tab => tab.path === '/home/work/quick-notes')
  const QuickNotesIcon = quickNotesHasEntries
    ? (quickNotesTabOpen ? QuickNotesFilledOpenIcon : QuickNotesFilledIcon)
    : (quickNotesTabOpen ? QuickNotesEmptyOpenIcon : QuickNotesEmptyIcon)
  const QuickNotesTabIcon = quickNotesHasEntries ? QuickNotesFilledIcon : QuickNotesEmptyIcon

  const handleOpenTab = (e: React.MouseEvent, id: string, path: string, title: string, icon?: TabIcon) => {
    if (e.metaKey || e.ctrlKey) {
      openTab({
        id: `${id}-${Date.now()}`,
        path,
        title,
        icon,
        lifeSpace: 'work',
        isDirty: false,
        isPinned: false
      })
    } else if (openPreviewTab) {
      openPreviewTab({ path, title, icon, lifeSpace: 'work' })
    } else {
      navigateActiveTab({ path, title, icon })
    }
  }

  const handleDoubleClick = (path: string) => {
    if (previewPaths.has(path)) {
      promotePreviewBySpace?.('work')
    } else if (!openPaths.has(path)) {
      promotePreviewBySpace?.('work')
    }
  }

  const handlePromote = promotePreviewBySpace ? () => promotePreviewBySpace('work') : undefined

  // Path-indexed sets derived from `tabs`. Memoized so sidebar re-renders
  // don't rebuild Sets on every tab switch.
  const { openPaths, previewPaths, pinnedPaths } = useMemo(() => {
    const open = new Set<string>()
    const preview = new Set<string>()
    const pinned = new Set<string>()
    const hasUnpinned = new Set<string>()
    for (const tab of tabs) {
      open.add(tab.path)
      if (tab.isPreview) preview.add(tab.path)
      if (tab.isPinned) pinned.add(tab.path)
      else hasUnpinned.add(tab.path)
    }
    for (const path of hasUnpinned) pinned.delete(path)
    return { openPaths: open, previewPaths: preview, pinnedPaths: pinned }
  }, [tabs])
  const DormantSidebarIcon = openPaths.has('/home/work/dormant') ? DormantOpenIcon : DormantIcon
  const UpcomingSidebarIcon = openPaths.has('/home/work/upcoming') ? UpcomingOpenIcon : UpcomingIcon

  return (
    <>
      <div className="standard-sidebar__quick-notes">
        <SidebarItem
          icon={QuickNotesIcon as unknown as LucideIcon}
          label={t('sidebar.quickNotes')}
          path="/home/work/quick-notes"
          isActive={activeTab?.path === '/home/work/quick-notes'}
          hasOpenTab={openPaths.has('/home/work/quick-notes')}
          isPreview={previewPaths.has('/home/work/quick-notes')}
          isPinned={pinnedPaths.has('/home/work/quick-notes')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/quick-notes') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/quick-notes') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/quick-notes') : undefined}
          onOpenNewTab={() => openTab({ id: `work-quick-notes-${Date.now()}`, path: '/home/work/quick-notes', title: t('sidebar.quickNotes'), icon: QuickNotesTabIcon as unknown as LucideIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => { if (!e.metaKey && !e.ctrlKey) dispatchAppEvent('quick-notes-exit-clarify', 'work'); handleOpenTab(e, 'work-quick-notes', '/home/work/quick-notes', t('sidebar.quickNotes'), QuickNotesTabIcon as unknown as LucideIcon) }}
          onDoubleClick={() => handleDoubleClick('/home/work/quick-notes')}
          onPromote={handlePromote}
        />
      </div>
      <SidebarSection
        id="work-overview"
        title={t('sidebar.workToday')}
        defaultExpanded
      >
        <SidebarItem
          icon={UpcomingSidebarIcon as unknown as LucideIcon}
          label={t('sidebar.upcoming')}
          badge={upcomingCount}
          path="/home/work/upcoming"
          isActive={activeTab?.path === '/home/work/upcoming'}
          hasOpenTab={openPaths.has('/home/work/upcoming')}
          isPreview={previewPaths.has('/home/work/upcoming')}
          isPinned={pinnedPaths.has('/home/work/upcoming')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/upcoming') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/upcoming') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/upcoming') : undefined}
          onOpenNewTab={() => openTab({ id: `work-upcoming-${Date.now()}`, path: '/home/work/upcoming', title: t('sidebar.upcoming'), icon: UpcomingIcon as unknown as LucideIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-upcoming', '/home/work/upcoming', t('sidebar.upcoming'), UpcomingIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/work/upcoming')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={DormantSidebarIcon as LucideIcon}
          label={t('sidebar.dormant')}
          path="/home/work/dormant"
          isActive={activeTab?.path === '/home/work/dormant'}
          hasOpenTab={openPaths.has('/home/work/dormant')}
          isPreview={previewPaths.has('/home/work/dormant')}
          isPinned={pinnedPaths.has('/home/work/dormant')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/dormant') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/dormant') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/dormant') : undefined}
          onOpenNewTab={() => openTab({ id: `work-dormant-${Date.now()}`, path: '/home/work/dormant', title: t('sidebar.dormant'), icon: DormantIcon as LucideIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-dormant', '/home/work/dormant', t('sidebar.dormant'), DormantIcon as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/work/dormant')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={CircleCheckBig}
          label={t('sidebar.done')}
          path="/home/work/done"
          isActive={activeTab?.path === '/home/work/done'}
          hasOpenTab={openPaths.has('/home/work/done')}
          isPreview={previewPaths.has('/home/work/done')}
          isPinned={pinnedPaths.has('/home/work/done')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/done') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/done') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/done') : undefined}
          onOpenNewTab={() => openTab({ id: `work-done-${Date.now()}`, path: '/home/work/done', title: t('sidebar.done'), icon: CircleCheckBig, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-done', '/home/work/done', t('sidebar.done'), CircleCheckBig)}
          onDoubleClick={() => handleDoubleClick('/home/work/done')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <SidebarSection
        id="work-development"
        title={t('sidebar.workSection')}
        defaultExpanded
      >
        {/* Gewohnheiten/Habits vorübergehend ausgeblendet — Route bleibt aktiv, Eintrag später wieder einkommentieren
        <SidebarItem
          icon={RocketIcon as unknown as LucideIcon}
          label={t('sidebar.habits')}
          path="/home/work/habits"
          isActive={activeTab?.path === '/home/work/habits'}
          hasOpenTab={openPaths.has('/home/work/habits')}
          isPreview={previewPaths.has('/home/work/habits')}
          isPinned={pinnedPaths.has('/home/work/habits')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/habits') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/habits') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/habits') : undefined}
          onOpenNewTab={() => openTab({ id: `work-habits-${Date.now()}`, path: '/home/work/habits', title: t('sidebar.habits'), icon: RocketIcon as unknown as LucideIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-habits', '/home/work/habits', t('sidebar.habits'), RocketIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/work/habits')}
          onPromote={handlePromote}
        />
        */}
        <SidebarItem
          icon={RecurringIcon as unknown as LucideIcon}
          label={t('sidebar.recurring')}
          path="/home/work/recurring"
          isActive={activeTab?.path === '/home/work/recurring'}
          hasOpenTab={openPaths.has('/home/work/recurring')}
          isPreview={previewPaths.has('/home/work/recurring')}
          isPinned={pinnedPaths.has('/home/work/recurring')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/recurring') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/recurring') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/recurring') : undefined}
          onOpenNewTab={() => openTab({ id: `work-recurring-${Date.now()}`, path: '/home/work/recurring', title: t('sidebar.recurring'), icon: RecurringIcon as unknown as LucideIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-recurring', '/home/work/recurring', t('sidebar.recurring'), RecurringIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/work/recurring')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={GoalsIcon}
          label={t('sidebar.goals')}
          path="/home/work/goals"
          isActive={activeTab?.path === '/home/work/goals'}
          hasOpenTab={openPaths.has('/home/work/goals')}
          isPreview={previewPaths.has('/home/work/goals')}
          isPinned={pinnedPaths.has('/home/work/goals')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/work/goals') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/work/goals') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/work/goals') : undefined}
          onOpenNewTab={() => openTab({ id: `work-goals-${Date.now()}`, path: '/home/work/goals', title: t('sidebar.goals'), icon: GoalsIcon, lifeSpace: 'work', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'work-goals', '/home/work/goals', t('sidebar.goals'), GoalsIcon)}
          onDoubleClick={() => handleDoubleClick('/home/work/goals')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <FavoritesSection
        lifeSpace="work"
        activeTab={activeTab}
        openTab={openTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
      />

      <ProjectsSection
        lifeSpace="work"
        activeTab={activeTab}
        openTab={openTab}
        navigateActiveTab={navigateActiveTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
      />
    </>
  )
}
